
import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../components/UI';
import { useAuth } from '../components/AuthContext';
import { useLeague } from '../components/LeagueContext';
import { UserRole } from '../types';
import { apiRequest } from '../utils/api';
import { BookOpen, CheckCircle, Upload, AlertTriangle, FileText, Download, BellRing, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { PageHero } from '../components/PageHero';

export const Rules: React.FC = () => {
    const { user, updateUserContext } = useAuth();
    const { settings, refreshSettings } = useLeague();
    const [uploading, setUploading] = useState(false);
    const [sendingReminder, setSendingReminder] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [needsAcceptance, setNeedsAcceptance] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    }, []);

    useEffect(() => {
        if (user && settings.rulebook_updated_at) {
            const rulesDate = new Date(settings.rulebook_updated_at).getTime();
            const userDate = user.rulebook_accepted_at ? new Date(user.rulebook_accepted_at).getTime() : 0;
            setNeedsAcceptance(userDate < rulesDate);
        } else if (user && !settings.rulebook_url) {
            setNeedsAcceptance(false); // No rules exist yet
        }
    }, [user, settings]);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (file.type !== 'application/pdf') {
            alert("Nur PDF Dateien erlaubt.");
            return;
        }

        if (file.size > 15 * 1024 * 1024) { // 15MB limit
            alert("Datei zu groß (Max 15MB).");
            return;
        }

        setUploading(true);
        const reader = new FileReader();
        reader.onloadend = async () => {
            try {
                await apiRequest('settings.php', 'POST', {
                    rulebook_url: reader.result as string
                });
                await refreshSettings();
                alert("Regelwerk erfolgreich hochgeladen.");
                if (fileInputRef.current) fileInputRef.current.value = '';
            } catch (err: any) {
                console.error(err);
                alert("Fehler beim Hochladen: " + (err.message || 'Unbekannter Fehler'));
            } finally {
                setUploading(false);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleAccept = async () => {
        if (!user) return;
        try {
            const res = await apiRequest<{user: any, message: string}>('profile.php', 'POST', {
                action: 'accept_rules',
                id: user.id
            });
            if (res.user) {
                updateUserContext(res.user);
                alert("Regelwerk akzeptiert!");
            }
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleRemindPending = async () => {
        if (!confirm("Sollen alle Fahrer, die das Regelwerk noch nicht akzeptiert haben, via Discord markiert werden?")) return;
        setSendingReminder(true);
        try {
            await apiRequest<{message: string, count: number}>('rules.php', 'POST', {
                action: 'remind_pending'
            });
            alert("Erinnerung gesendet!");
        } catch (e: any) {
            alert(e.message);
        } finally {
            setSendingReminder(false);
        }
    };

    const getUrl = (url?: string | null) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
        
        return cleanBase ? `${cleanBase}/${cleanUrl}` : cleanUrl;
    };

    const pdfUrl = getUrl(settings.rulebook_url);
    const isAdmin = user?.roles.includes(UserRole.ADMIN) || user?.roles.includes(UserRole.WEBADMIN);

    return (
        <div className="sdc-feature-page sdc-rules-page max-w-7xl mx-auto space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Sportliches Fundament"
                title="Ligaregelwerk"
                description="Die verbindliche Grundlage für faire Rennen, klare Abläufe und ein respektvolles Miteinander im Sunday Driver Cup."
                icon={BookOpen}
                accent="#a855f7"
                stats={[
                    { label: 'Dokument', value: pdfUrl ? 'PDF' : '–' },
                    { label: 'Status', value: needsAcceptance ? 'Offen' : 'Aktuell' },
                ]}
                actions={pdfUrl ? (
                    <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white transition hover:bg-white/15"
                    >
                        <Download size={17}/> PDF öffnen
                    </a>
                ) : undefined}
            />

            {needsAcceptance && (
                <div className="flex flex-col items-center justify-between gap-5 overflow-hidden rounded-[1.75rem] border border-red-500/30 bg-[linear-gradient(120deg,rgba(239,68,68,.18),rgba(18,18,24,.96)_55%)] p-6 shadow-2xl shadow-red-950/20 md:flex-row">
                    <div className="flex items-center gap-4">
                        <div className="rounded-[1rem] bg-red-500 p-3 shadow-lg shadow-red-950/40">
                            <AlertTriangle className="h-7 w-7 text-white"/>
                        </div>
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.24em] text-red-300">Bestätigung ausstehend</p>
                            <h3 className="mt-1 text-xl font-black text-white">Aktuelles Regelwerk akzeptieren</h3>
                            <p className="mt-1 max-w-2xl text-sm text-red-100/75">Lies die aktuelle Ausgabe und bestätige sie anschließend. Erst danach ist eine Rennanmeldung wieder möglich.</p>
                        </div>
                    </div>
                    <Button onClick={handleAccept} className="w-full border-none bg-white px-7 py-4 text-base font-black text-gray-950 shadow-xl hover:bg-red-50 md:w-auto">
                        <CheckCircle size={21} className="mr-2"/> Gelesen & akzeptiert
                    </Button>
                </div>
            )}

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
                <section className="sdc-feature-panel min-w-0 overflow-hidden p-2 sm:p-3">
                    <div className="flex items-center justify-between gap-4 border-b border-white/[0.07] px-4 py-4 sm:px-5">
                        <div className="flex items-center gap-3">
                            <span className="grid h-11 w-11 place-items-center rounded-[0.9rem] bg-purple-500/15 text-purple-300">
                                <FileText size={20}/>
                            </span>
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-purple-300">Offizielles Dokument</p>
                                <h2 className="text-base font-black text-white">Aktuelle Ausgabe</h2>
                            </div>
                        </div>
                        {pdfUrl && <span className="hidden rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-gray-400 sm:inline-flex">PDF · Online</span>}
                    </div>

                    <div className="flex min-h-[68vh] w-full flex-col overflow-hidden rounded-b-[1.45rem] bg-[#111116]" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {pdfUrl ? (
                            isMobile ? (
                                <div className="flex flex-1 flex-col items-center justify-center gap-5 px-7 py-16 text-center">
                                    <div className="grid h-20 w-20 place-items-center rounded-[1.6rem] border border-purple-400/20 bg-purple-500/10 text-purple-300">
                                        <BookOpen size={34}/>
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-white">Regelwerk bereit</h3>
                                        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-400">Auf deinem Gerät öffnet sich das Dokument am zuverlässigsten in der PDF-Ansicht des Browsers.</p>
                                    </div>
                                    <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-purple-500 px-6 py-3 text-sm font-black text-white shadow-lg shadow-purple-950/40 transition hover:bg-purple-400">
                                        <Download size={18}/> Regelwerk öffnen
                                    </a>
                                </div>
                            ) : (
                                <iframe
                                    src={`${pdfUrl}#toolbar=0&view=FitH`}
                                    className="h-full min-h-[68vh] w-full border-none bg-[#2d2d35]"
                                    title="Rulebook"
                                />
                            )
                        ) : (
                            <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-20 text-center text-gray-500">
                                <FileText size={58} className="opacity-20"/>
                                <div>
                                    <p className="text-xl font-black text-white">Noch kein Regelwerk hinterlegt</p>
                                    <p className="mt-2 text-sm text-gray-500">Sobald eine PDF hochgeladen wurde, erscheint sie hier.</p>
                                </div>
                                {isAdmin && <Button size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()}>Jetzt hochladen</Button>}
                            </div>
                        )}
                    </div>
                </section>

                <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
                    <div className="sdc-bento-card p-6">
                        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-gray-500">Dein Status</p>
                        <div className="mt-5 flex items-center gap-4">
                            <span className={`grid h-12 w-12 place-items-center rounded-[1rem] ${needsAcceptance ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                                {needsAcceptance ? <AlertTriangle size={23}/> : <CheckCircle size={23}/>} 
                            </span>
                            <div>
                                <p className="font-black text-white">{needsAcceptance ? 'Bestätigung offen' : 'Alles aktuell'}</p>
                                <p className="text-xs text-gray-500">{needsAcceptance ? 'Bitte Regelwerk lesen' : 'Keine Aktion notwendig'}</p>
                            </div>
                        </div>
                        {needsAcceptance && (
                            <Button onClick={handleAccept} className="mt-5 w-full border-none bg-purple-500 font-black text-white hover:bg-purple-400">
                                <CheckCircle size={17} className="mr-2"/> Jetzt akzeptieren
                            </Button>
                        )}
                    </div>

                    {isAdmin && (
                        <div className="sdc-bento-card p-6">
                            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-purple-300">Liga-Administration</p>
                            <h3 className="mt-2 text-lg font-black text-white">Dokument verwalten</h3>
                            <p className="mt-2 text-sm leading-6 text-gray-500">Eine neue PDF setzt die Bestätigung für alle Fahrer erneut auf offen.</p>
                            <input type="file" ref={fileInputRef} className="hidden" accept="application/pdf" onChange={handleUpload}/>
                            <div className="mt-5 grid gap-2">
                                <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full border-none bg-purple-500 font-black text-white hover:bg-purple-400">
                                    {uploading ? <><Loader2 size={17} className="mr-2 animate-spin"/> Wird hochgeladen</> : <><Upload size={17} className="mr-2"/> PDF aktualisieren</>}
                                </Button>
                                <Button variant="secondary" onClick={handleRemindPending} disabled={sendingReminder} className="w-full border border-white/10 bg-white/[0.04] font-bold text-gray-200 hover:bg-white/[0.08]">
                                    {sendingReminder ? <Loader2 size={17} className="mr-2 animate-spin"/> : <BellRing size={17} className="mr-2"/>}
                                    Säumige erinnern
                                </Button>
                            </div>
                        </div>
                    )}

                    {pdfUrl && (
                        <a href={pdfUrl} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-[1.3rem] border border-white/[0.08] bg-white/[0.025] px-5 py-4 text-sm font-bold text-gray-400 transition hover:border-purple-400/30 hover:text-white">
                            PDF extern öffnen <Download size={17}/>
                        </a>
                    )}
                </aside>
            </div>
        </div>
    );
};
